import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Tooltip
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { formatSubmittedAt } from './utils';

const ResultsTable = ({ examData, submissions, selectedSubmission, setSelectedSubmission, calculateSectionScore }) => {
  // Defensive checks
  if (!examData || !Array.isArray(examData.sections) || !Array.isArray(submissions) || typeof calculateSectionScore !== 'function') {
    return (
      <div style={{ padding: 32, color: '#d32f2f', fontWeight: 700, fontSize: 18 }}>
        Assessment results data is missing or invalid. Please check your assessment data and submissions.
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', width: '100%', position: 'relative' }}>
      <TableContainer component={Paper} style={{ maxWidth: '100%' }}>
        <Table size="small" style={{ fontSize: 12 }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 150, maxWidth: 180, position: 'sticky', left: 0, background: '#fff', zIndex: 2, borderRight: '1px solid #e0e0e0' }}>Name</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 200, maxWidth: 250, position: 'sticky', left: 150, background: '#fff', zIndex: 2, borderRight: '1px solid #e0e0e0' }}>Email</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 14, minWidth: 100, maxWidth: 120, background: '#e3f2fd', color: '#1976d2', textAlign: 'center', position: 'sticky', left: 350, zIndex: 3 }}>Total %</TableCell>
              {examData.sections.map((section, idx) => (
                <TableCell key={idx} align="center" style={{ fontWeight: 700, fontSize: 12, minWidth: 160, maxWidth: 180, padding: '8px 4px' }}>
                  <div style={{ fontSize: 11, lineHeight: '1.2', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <div style={{ fontWeight: 700, color: '#1976d2' }}>{section.title}</div>
                    <div style={{ color: '#666', fontSize: 10 }}>({section.weightage}%)</div>
                  </div>
                </TableCell>
              ))}
              <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 120, maxWidth: 140 }}>Security Violations</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 140, maxWidth: 160 }}>Submission Time</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 80, maxWidth: 100, position: 'sticky', right: 0, background: '#fff', zIndex: 2, borderLeft: '1px solid #e0e0e0' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.map((submission, index) => {
              const name = submission.name || submission.sname || 'N/A';
              const email = submission.email || submission.semail || 'N/A';
              let sectionScores = [];
              if (Array.isArray(examData.sections)) {
                sectionScores = examData.sections.map(section => {
                  try {
                    return calculateSectionScore(section, submission) || {};
                  } catch {
                    return {};
                  }
                });
              }
              // Always calculate totalPercentage as the sum of section weighted scores
              const totalPercentage = sectionScores.reduce((sum, sec) => sum + (sec && sec.weightedScore ? sec.weightedScore : 0), 0);
              return (
                <TableRow key={index} style={{ fontSize: 12 }}>
                  <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 150, maxWidth: 180, position: 'sticky', left: 0, background: '#fff', zIndex: 1, wordWrap: 'break-word', overflowWrap: 'break-word', borderRight: '1px solid #e0e0e0' }}>
                    {name}
                  </TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 200, maxWidth: 250, position: 'sticky', left: 150, background: '#fff', zIndex: 1, wordWrap: 'break-word', overflowWrap: 'break-word', borderRight: '1px solid #e0e0e0' }}>
                    {email}
                  </TableCell>
                  <TableCell style={{ fontWeight: 900, fontSize: 15, color: '#1976d2', textAlign: 'center', minWidth: 100, maxWidth: 120, background: '#fff', letterSpacing: 1, position: 'sticky', left: 350, zIndex: 2 }}>
                    {totalPercentage.toFixed(2)}%
                  </TableCell>
                  {sectionScores.map((sec, sidx) => (
                    <TableCell key={sidx} align="center" style={{ fontWeight: 700, fontSize: 12, minWidth: 160, maxWidth: 180, padding: '8px 4px' }}>
                      <div style={{ fontSize: 11, lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <div style={{ fontWeight: 700, color: '#1976d2' }}>
                          {sec.rawScore ?? 0}/{sec.maxScore ?? 0}
                        </div>
                        <div style={{ color: '#43a047', fontWeight: 700, fontSize: 10 }}>
                          {sec.maxScore > 0 ? ((sec.rawScore / sec.maxScore) * 100).toFixed(1) : '0.0'}%
                        </div>
                        <div style={{ color: '#666', fontSize: 9 }}>
                          W: {sec.weightedScore?.toFixed(1) || '0.0'}%
                        </div>
                      </div>
                    </TableCell>
                  ))}
                  <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 120, maxWidth: 140 }}>
                    <div style={{ fontSize: '10px', lineHeight: '1.2' }}>
                      <div><strong>Total:</strong> {submission.totalCheatCount || 0}</div>
                      <div><strong>C/P:</strong> {submission.copyPaste || 0}</div>
                      <div><strong>Tab:</strong> {submission.tab || 0}</div>
                      <div><strong>Cam:</strong> {submission.webcamOff || 0}</div>
                    </div>
                  </TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 140, maxWidth: 160 }}>{
                    submission.submittedAt ? formatSubmittedAt(submission.submittedAt) : ''
                  }</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 12, minWidth: 80, maxWidth: 100, position: 'sticky', right: 0, background: '#fff', zIndex: 1, borderLeft: '1px solid #e0e0e0' }}>
                    <Tooltip title={selectedSubmission === submission ? 'Hide Details' : 'View Details'}>
                      <IconButton
                        color="primary"
                        onClick={() => setSelectedSubmission(selectedSubmission === submission ? null : submission)}
                        size="small"
                        style={{ background: '#f5faff', border: '1px solid #90caf9', borderRadius: 6 }}
                      >
                        {selectedSubmission === submission ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      

    </div>
  );
};

export default ResultsTable; 